import { useAppSelector, useAppDispatch } from "@app/hooks";
import { useCountDown } from "@helpers/countHelper";
import { NavItems } from "@helpers/navigation";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { BasicProps } from "../../../common/components/ComponentsInterface";
import { setSiltentTimeout } from "../../../common/store/assessmentFlowSlice";
import { trackDiagnosticTrace } from "@utils/diagnostics/appInsightsDiagnostics";

const SOUND_THRESHOLD = 0.15;
const SOUND_RESET_THROTTLE_MS = 1000;
const SOUND_LOG_THROTTLE_MS = 10000;

export const AssessmentTimeout = ({ activityKey, micVolume }: AssessmentTimeoutProps): JSX.Element => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const silentTimeout = useAppSelector((x) => x.assessmentFlow.silentTimeout);
    const globalSettings = useAppSelector((x) => x.globalSettings);
    const lastSoundResetAt = useRef(0);
    const lastSoundLogAt = useRef(0);

    const paths = [
        NavItems.Assessments.AssessmentPresentTargets.path.toLowerCase(),
        NavItems.Assessments.AssessmentFreeRecall.path.toLowerCase(),
        NavItems.Assessments.AssessmentCuedRecalls.path.toLowerCase()
    ];
    const currentPath = location.pathname.toLowerCase();
    const checkTimeout = paths.includes(currentPath);

    const handleOnTimeout = (): void => {
        if (checkTimeout) {
            trackDiagnosticTrace({
                enabled: globalSettings.enableSttDiagnostics,
                area: "assessment-timeout",
                event: "silent-timeout:expired",
                properties: {
                    counter,
                    currentPath
                }
            });
            navigate(NavItems.Login.Landing.path);
        }
    };
    const [counter, resetTimeout] = useCountDown({
        from: silentTimeout,
        onDone: handleOnTimeout
    });

    const resetSilentTimeout = (): void => {
        resetTimeout(globalSettings.assessmentTimeout);
        dispatch(setSiltentTimeout(globalSettings.assessmentTimeout));
    };

    useEffect(() => {
        if (checkTimeout) {
            dispatch(setSiltentTimeout(counter));
        }
    }, [counter]);

    useEffect(() => {
        if (!checkTimeout || activityKey === undefined) {
            return;
        }

        resetSilentTimeout();

        trackDiagnosticTrace({
            enabled: globalSettings.enableSttDiagnostics,
            area: "assessment-timeout",
            event: "silent-timeout:activity-reset",
            properties: {
                activityKey,
                currentPath,
                counter
            }
        });
    }, [activityKey]);

    useEffect(() => {
        if (!checkTimeout || !micVolume || micVolume <= SOUND_THRESHOLD) {
            return;
        }

        const now = Date.now();
        const shouldResetTimeout = now - lastSoundResetAt.current >= SOUND_RESET_THROTTLE_MS;
        const shouldLogSoundDetected = now - lastSoundLogAt.current >= SOUND_LOG_THROTTLE_MS;

        if (shouldResetTimeout) {
            lastSoundResetAt.current = now;
            resetSilentTimeout();
        }

        if (shouldLogSoundDetected) {
            lastSoundLogAt.current = now;
            trackDiagnosticTrace({
                enabled: globalSettings.enableSttDiagnostics,
                area: "assessment-timeout",
                event: "silent-timeout:sound-reset",
                properties: {
                    micVolume,
                    currentPath,
                    counter
                }
            });
        }
    }, [micVolume]);

    return <></>;
};

interface AssessmentTimeoutProps extends BasicProps {
    activityKey?: string | number | boolean;
    micVolume?: number;
}
